import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogSurface,
  DialogTitle,
  Field,
  Text,
} from "@fluentui/react-components";
import { ConstantValues } from "common/constants";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { TagPickerCustom } from "components/fields/tagPicker/TagPicker";
import { IWorkspace } from "models/IWorkspace";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { useMoveDialogStyles } from "./MoveConversation.styles";
import { IMoveConversationDialogProps } from "./MoveConversationDialog.types";

export const MoveConversationDialog = (props: IMoveConversationDialogProps) => {
  const workspaces = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const activeWorkspace = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );
  const localStyles = useMoveDialogStyles();
  const buttonStyles = useSharedButtonStyles();

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<
    string | undefined
  >(undefined);

  const pickerItems: CustomOption[] = useMemo(() => {
    return workspaces
      .filter(
        (w: IWorkspace) =>
          w.id !== activeWorkspace?.id &&
          !w.archived &&
          w.name !== ConstantValues.PERSONAL_WORKSPACE,
      )
      .sort((a: IWorkspace, b: IWorkspace) => a.name.localeCompare(b.name))
      .map(
        (w: IWorkspace): CustomOption => ({
          key: w.id,
          text: w.name,
        }),
      );
  }, [workspaces, activeWorkspace]);

  useEffect(() => {
    if (!props.isOpen) setSelectedWorkspaceId(undefined);
  }, [props.isOpen]);

  const handleSelectionChange = (items: CustomOption[]) => {
    const first = items[0];
    setSelectedWorkspaceId(first ? String(first.key) : undefined);
  };

  const selectedItems: CustomOption[] = useMemo(() => {
    if (!selectedWorkspaceId) return [];
    const ws = workspaces.find((w) => w.id === selectedWorkspaceId);
    return ws ? [{ key: ws.id, text: ws.name }] : [];
  }, [selectedWorkspaceId, workspaces]);

  const handleMove = async (): Promise<void> => {
    if (selectedWorkspaceId) {
      await props.onMove(selectedWorkspaceId);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) props.onClose();
      }}
    >
      <DialogSurface className={localStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle>Move Chat</DialogTitle>

          {pickerItems.length === 0 ? (
            <Text size={200}>No eligible workspaces found.</Text>
          ) : (
            <div className={localStyles.workspacePickerWrapper}>
              <Field label="Select Workspace">
                <TagPickerCustom
                  items={pickerItems}
                  selectedItems={selectedItems}
                  onSelectionChange={handleSelectionChange}
                  singleSelect
                />
              </Field>
            </div>
          )}

          <DialogActions>
            <button
              className={buttonStyles.secondaryButton}
              onClick={props.onClose}
            >
              Cancel
            </button>
            <button
              className={buttonStyles.primaryButton}
              disabled={!selectedWorkspaceId}
              onClick={handleMove}
            >
              Move
            </button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
